import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvents } from '../context/EventsContext';
import { formatDate, formatPrice } from '../utils/format';
import { Calendar, Clock, Users, ChevronRight, Sparkles } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const EventCard = ({ event, index }) => {
  const navigate = useNavigate();
  const isSoldOut = event.status === 'sold_out';

  return (
    <div
      data-testid={`event-card-${event.id}`}
      className={`group relative overflow-hidden rounded-lg bg-secondary border border-border 
        transition-all duration-500 hover:border-primary/50 hover:shadow-[0_0_30px_rgba(234,88,12,0.15)]
        animate-fade-in ${isSoldOut ? 'opacity-60 grayscale' : ''}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Image Container */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={event.image}
          alt={event.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Status Badge */}
        <Badge
          data-testid={`event-status-${event.id}`}
          className={`absolute top-4 right-4 ${
            isSoldOut 
              ? 'bg-muted text-muted-foreground' 
              : 'bg-primary text-primary-foreground animate-pulse-glow'
          }`}
        >
          {isSoldOut ? 'Agotado' : 'Reservas Abiertas'}
        </Badge>

        {/* Price Tag */}
        <div className="absolute bottom-4 left-4">
          <span className="text-xs text-white/60">desde </span>
          <span className="text-2xl font-bold text-white">
            {formatPrice(event.priceFrom, event.currency)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors duration-300 font-serif">
          {event.name}
        </h3>
        
        <p className="text-muted-foreground text-sm line-clamp-2">
          {event.description}
        </p>

        {/* Event Details */}
        <div className="flex flex-wrap gap-4 text-sm text-secondary-foreground">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <span>{formatDate(event.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <span>{event.time} hrs</span>
          </div>
        </div>

        {/* Availability */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <span className="text-secondary-foreground">
              {isSoldOut ? 'Sin disponibilidad' : `${event.availableChairs} sillas disponibles`}
            </span>
          </div>
          
          <Button
            data-testid={`view-event-btn-${event.id}`}
            variant={isSoldOut ? "outline" : "default"}
            size="sm"
            disabled={isSoldOut}
            onClick={() => navigate(`/evento/${event.id}`)}
            className={`group/btn ${!isSoldOut && 'bg-primary hover:bg-accent'}`}
          >
            {isSoldOut ? 'Agotado' : 'Ver Evento'}
            {!isSoldOut && <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover/btn:translate-x-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [filter, setFilter] = useState('all');
  const { events } = useEvents();

  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(e => e.status === filter);

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
        
        {/* Radial Glow */}
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(234, 88, 12, 0.2) 0%, transparent 60%)'
          }}
        />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium">
              Experiencias Gastronómicas
            </span>
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight">
            Reserva Tu
            <span className="block text-primary">Momento</span>
          </h1>
          
          <p className="text-xl text-white/70 max-w-2xl mx-auto mb-8">
            Descubre eventos exclusivos y reserva tu silla para crear recuerdos inolvidables
          </p>

          <Button
            data-testid="explore-events-btn"
            size="lg"
            className="bg-primary hover:bg-accent text-white px-8 py-6 text-lg glow-orange"
            onClick={() => document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Explorar Eventos
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-primary uppercase tracking-[0.2em] text-sm font-medium">
              Próximos Eventos
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground mt-2">
              Experiencias Únicas
            </h2>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2" data-testid="event-filters">
            <Button
              data-testid="filter-all"
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-primary' : 'border-border hover:border-primary/50'}
            >
              Todos
            </Button>
            <Button
              data-testid="filter-open"
              variant={filter === 'open' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('open')}
              className={filter === 'open' ? 'bg-primary' : 'border-border hover:border-primary/50'}
            >
              Disponibles
            </Button>
            <Button
              data-testid="filter-sold-out"
              variant={filter === 'sold_out' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('sold_out')}
              className={filter === 'sold_out' ? 'bg-primary' : 'border-border hover:border-primary/50'}
            >
              Agotados
            </Button>
          </div>
        </div>

        {/* Events Grid - Asymmetric Tetris Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredEvents.map((event, index) => (
            <EventCard key={event.id} event={event} index={index} />
          ))}
        </div>

        {filteredEvents.length === 0 && (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">No hay eventos disponibles con este filtro</p>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-serif font-bold text-primary">Reserva</span>
            <span className="text-muted-foreground">© 2026</span>
          </div>
          <p className="text-muted-foreground text-sm">
            Sistema de reservaciones para restaurantes
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
